import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
    NgbAccordionBody,
    NgbAccordionButton,
    NgbAccordionCollapse,
    NgbAccordionDirective,
    NgbAccordionHeader,
    NgbAccordionItem,
} from '@ng-bootstrap/ng-bootstrap';
import { _, TranslateModule } from '@ngx-translate/core';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { PictureComponent } from 'src/app/components/picture/picture.component';
import { TagNamePipe } from 'src/app/pipes/tag-name.pipe';
import { AlertUtils } from 'src/app/utils/alert.utils';
import { CategoryMediaTag } from '../../../../models/category';
import { CreateOfferData } from '../../../../models/create-offer/create-offer-data';
import { FilterValueType } from '../../../../models/filter';
import { OfferVariantGroup } from '../../../../models/offer';
import { MediaService, TagDefinition } from '../../../../services/media.service';
import { ToastService } from '../../../../services/toast.service';
import { CategoryUtils } from '../../../../utils/category.utils';
import {
    liftWizardMediasFromVariantGroups,
    reattachWizardMediasToVariantGroups,
    stampWizardMediaGroups,
    WizardMedia,
} from '../../../../utils/create-offer-wizard-restore.utils';
import { OfferPhotoValidator } from '../../../../validators/offer-photo.validator';

export type TagSelection = { value: string; tag: TagDefinition; tagValue: FilterValueType };

@Component({
    selector: 'app-pick-medias',
    templateUrl: './pick-medias.component.html',
    styleUrls: ['./pick-medias.component.scss'],
    imports: [
        CommonModule,
        CommonModule,
        NgbAccordionBody,
        NgbAccordionButton,
        NgbAccordionCollapse,
        NgbAccordionDirective,
        NgbAccordionHeader,
        NgbAccordionItem,
        ReactiveFormsModule,
        TranslateModule,
        LoaderComponent,
        TagNamePipe,
        PictureComponent,
    ],
})
export class PickMediasComponent implements OnInit {
    @Output() nextStep = new EventEmitter<CreateOfferData>();
    @Input() data: CreateOfferData;
    @Input() isEdit = false;
    protected form = new FormGroup(
        {
            medias: new FormControl([]),
            offerVariantGroupsMedias: new FormControl([]),
            mediaTag: new FormControl(''),
        },
        [OfferPhotoValidator],
    );
    protected mediaTag: CategoryMediaTag;
    protected hasVariants: boolean;
    protected uploadingImages: Array<{ id: string; filename: string; progress?: number }> = [];
    protected mediaTags: TagSelection[];
    protected variantGroups: OfferVariantGroup[];
    protected selectedVariantGroup: OfferVariantGroup;
    protected submitted = false;

    constructor(
        private readonly mediaService: MediaService,
        private readonly toastService: ToastService,
    ) {}

    ngOnInit() {
        this.mediaTag = CategoryUtils.findProperty(this.data.categoryData.category, 'mediaTag');
        this.hasVariants = (this.data.filterData?.variants || []).length > 0;
        this.variantGroups = this.data.filterData?.variantGroups || [];
        this.selectedVariantGroup = this.variantGroups?.[0] || null;
        this.createMediaTags();
        const existing = (this.data?.mediaData?.medias || []) as WizardMedia[];
        if (existing.length > 0) {
            this.form.patchValue({ medias: [...existing] });
        } else if (this.variantGroups.length > 0) {
            const lifted = liftWizardMediasFromVariantGroups(this.variantGroups);
            if (lifted.length > 0) {
                this.form.patchValue({ medias: lifted });
            }
        }
        this.reattachMediasToVariantGroups();
        if (this.variantGroups.length > 0) {
            this.form.get('offerVariantGroupsMedias').setValue(this.variantGroups);
        }
        if ((this.form.get('medias')?.value || []).length > 0) {
            this.persistMediasToOfferData();
        }
    }

    async pickPhoto(): Promise<void> {
        // In root,
        if (this.mediaTag && !this.form.get('mediaTag').value) {
            await this.toastService.error(_('Please select picture type first'));
            return;
        }
        const variantGroupCode = this.selectedVariantGroup?.code;
        const selectedMediaTag = this.form.get('mediaTag').value || null;

        this.mediaService
            .pickMultipleImages()
            .then(async (images) => {
                if (!images || images.length === 0) {
                    return;
                }

                console.log(`Selected ${images.length} image(s)`);

                // Create placeholder entries for each image being uploaded
                this.uploadingImages = images.map((img, index) => ({
                    id: `upload-${Date.now()}-${index}`,
                    filename: img.filename,
                    progress: 0,
                }));

                if (images.length > 1) {
                    // Upload in parallel when multiple images were selected
                    const uploadPromises = images.map((image, index) => {
                        const uploadId = this.uploadingImages[index].id;
                        return new Promise<{ success: boolean; media?: any; uploadId: string }>((resolve) => {
                            this.mediaService.upload(
                                image.blob,
                                image.filename,
                                'offer',
                                () => {}, // Progress
                                (media) => {
                                    this.addUploadedMedia(media, variantGroupCode);

                                    // Remove this upload from the uploading list
                                    this.uploadingImages = this.uploadingImages.filter((u) => u.id !== uploadId);
                                    resolve({ success: true, media, uploadId });
                                }, // Success
                                (err) => {
                                    console.error('Upload failed for image:', image.filename, err);
                                    // Remove this upload from the uploading list
                                    this.uploadingImages = this.uploadingImages.filter((u) => u.id !== uploadId);
                                    resolve({ success: false, uploadId });
                                }, // Error
                                selectedMediaTag,
                            );
                        });
                    });

                    // Wait for all uploads to complete and handle errors
                    const results = await Promise.all(uploadPromises);

                    // Check if any uploads failed
                    const hasErrors = results.some((result) => !result.success);
                    if (hasErrors) {
                        AlertUtils.showError(_('An error occured while uploading the picture'));
                    }
                } else {
                    // Single image: upload sequentially
                    for (let i = 0; i < images.length; i++) {
                        const image = images[i];
                        const uploadId = this.uploadingImages[i].id;

                        await new Promise<void>((resolveUpload) => {
                            this.mediaService.upload(
                                image.blob,
                                image.filename,
                                'offer',
                                () => {}, // Progress
                                (media) => {
                                    // Remove this upload from the uploading list
                                    this.uploadingImages = this.uploadingImages.filter((u) => u.id !== uploadId);

                                    this.addUploadedMedia(media, variantGroupCode);

                                    resolveUpload();
                                }, // Success
                                (err) => {
                                    // Remove this upload from the uploading list
                                    this.uploadingImages = this.uploadingImages.filter((u) => u.id !== uploadId);

                                    if (err === 'upload-error') {
                                        AlertUtils.showError(_('An error occured while uploading the picture'));
                                    }
                                    resolveUpload();
                                }, // Error
                                selectedMediaTag,
                            );
                        });
                    }
                }

                // All uploads complete - uploadingImages should be empty now
            })
            .catch((err) => {
                console.log(err);
                // Clear any remaining uploads on error
                this.uploadingImages = [];
            });
    }

    async removeMedia(id: number) {
        // Remove from form
        const medias = this.form.get('medias');
        medias.setValue(medias.value.filter((m) => m.id !== id));

        if (this.variantGroups.length > 0) {
            // Remove from selected offer variant group
            this.selectedVariantGroup.medias = this.selectedVariantGroup.medias.filter((m) => m.id !== id);
            // Update the form
            this.form.get('offerVariantGroupsMedias').setValue(this.variantGroups);
        }

        this.persistMediasToOfferData();
        await this.toastService.success(_('The picture has been removed'));
    }

    private createMediaTags(): void {
        if (!this.mediaTag) {
            this.mediaTags = [];
            return;
        }
        const tags: TagSelection[] = [];
        for (const tag of MediaService.mediaTagDefinitions[this.mediaTag]) {
            if (!tag.filterBased) {
                tags.push({
                    value: tag.name,
                    tag: tag,
                    tagValue: null,
                });
                continue;
            }
            const filter = this.data.filterData.filters.find((f) => f.mediaTag === tag.name);
            if (!filter) {
                continue;
            }
            let value = this.data.filterData.filterValues[filter.getCode()];
            if (value && tag.type === 'number') {
                const v = parseInt(value as string, 10);
                value = v > 0 ? v : 0;
                for (let i = 0; i < v; i++) {
                    const n = i + 1;
                    tags.push({
                        value: v === 1 ? tag.name : tag.name + '|' + n,
                        tag: tag,
                        tagValue: v,
                    });
                }
            }
            if (value && tag.type === 'boolean') {
                tags.push({
                    value: tag.name,
                    tag: tag,
                    tagValue: value,
                });
            }
        }
        this.mediaTags = tags;
    }

    isAddMediaButtonEnabled(): boolean {
        // We should not be loading media already
        if (this.uploadingImages.length > 0) {
            return false;
        }

        // If we have media tags, we need to check we have selection
        if (this.mediaTag && !this.form.get('mediaTag').value) {
            return false;
        }

        // If we have offers, we need to check we have selection
        return !(this.variantGroups?.length > 1 && !this.selectedVariantGroup);
    }

    accordionItemToggled(code: string, mode: 'hide' | 'show') {
        console.log(code, mode);
        if (mode === 'hide') {
            this.selectedVariantGroup = null;
            return;
        }

        if (mode === 'show') {
            this.selectedVariantGroup = this.variantGroups.find((o) => o.code === code);
        }
    }

    submit() {
        this.submitted = true;
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.persistMediasToOfferData();

        this.nextStep.emit(this.data);
    }

    private addUploadedMedia(media: WizardMedia, variantGroupCode?: string): void {
        if (variantGroupCode) {
            media.group = variantGroupCode;
        }
        const medias = this.form.get('medias');
        medias.setValue([...(medias.value || []), media]);

        if (this.variantGroups.length > 0 && variantGroupCode) {
            const group = this.variantGroups.find((g) => g.code === variantGroupCode);
            if (group) {
                group.medias = group.medias || [];
                group.medias.push(media as (typeof group.medias)[number]);
                this.form.get('offerVariantGroupsMedias').setValue(this.variantGroups);
            }
        }

        this.persistMediasToOfferData();
    }

    private persistMediasToOfferData(): void {
        const medias = [...(this.form.get('medias')?.value || [])] as WizardMedia[];
        stampWizardMediaGroups(medias, this.variantGroups);
        this.data.mediaData.medias = medias;
    }

    private reattachMediasToVariantGroups(): void {
        const existing = (this.data?.mediaData?.medias || []) as WizardMedia[];
        reattachWizardMediasToVariantGroups(this.variantGroups, existing);
        if (this.variantGroups?.length) {
            this.form.get('offerVariantGroupsMedias').setValue(this.variantGroups);
        }
    }
}
